/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable no-use-before-define */
import { Vector2 } from '../vector/Vector2';

export class Box2 {
  public min: Vector2;
  public max: Vector2;

  constructor(min: Vector2, max: Vector2) {
    this.min =
      min !== undefined ? min : new Vector2(Number(Infinity), Number(Infinity));
    this.max = max !== undefined ? max : new Vector2(-Infinity, -Infinity);
  }

  public set(min: Vector2, max: Vector2): this {
    this.min.copy(min);
    this.max.copy(max);

    return this;
  }

  public setFromPoints(points: Vector2[]): this {
    this.makeEmpty();

    for (let i = 0, il = points.length; i < il; i++) {
      this.expandByPoint(points[i]);
    }

    return this;
  }

  public setFromCenterAndSize(center: Vector2, size: Vector2): this {
    const halfSize = v1.copy(size).multiplyScalar(0.5);

    this.min.copy(center).sub(halfSize);
    this.max.copy(center).add(halfSize);

    return this;
  }

  public clone(): Box2 {
    return new Box2(this.min, this.max);
  }

  public copy(box: Box2): this {
    this.min.copy(box.min);
    this.max.copy(box.max);

    return this;
  }

  public makeEmpty(): this {
    this.min.x = this.min.y = Number(Infinity);
    this.max.x = this.max.y = -Infinity;

    return this;
  }

  public isEmpty(): boolean {
    // this is a more robust check for empty than ( volume <= 0 ) because volume can get positive with two negative axes

    return this.max.x < this.min.x || this.max.y < this.min.y;
  }

  public getCenter(out: Vector2): Vector2 {
    return this.isEmpty()
      ? out.set(0, 0)
      : out.addVectors(this.min, this.max).multiplyScalar(0.5);
  }

  public getSize(out: Vector2): Vector2 {
    return this.isEmpty() ? out.set(0, 0) : out.subVectors(this.max, this.min);
  }

  public expandByPoint(point: Vector2): this {
    this.min.min(point);
    this.max.max(point);

    return this;
  }

  public expandByVector(vector: Vector2): this {
    this.min.sub(vector);
    this.max.add(vector);

    return this;
  }

  public expandByScalar(scalar: number): this {
    this.min.addScalar(-scalar);
    this.max.addScalar(scalar);

    return this;
  }

  public containsPoint(point: Vector2): boolean {
    return !(
      point.x < this.min.x ||
      point.x > this.max.x ||
      point.y < this.min.y ||
      point.y > this.max.y
    );
  }

  public containsBox(box: Box2): boolean {
    return (
      this.min.x <= box.min.x &&
      box.max.x <= this.max.x &&
      this.min.y <= box.min.y &&
      box.max.y <= this.max.y
    );
  }

  public getParameter(point: Vector2, out: Vector2): Vector2 {
    // This can potentially have a divide by zero if the box
    // has a size dimension of 0.

    return out.set(
      (point.x - this.min.x) / (this.max.x - this.min.x),
      (point.y - this.min.y) / (this.max.y - this.min.y),
    );
  }

  public intersectsBox(box: Box2): boolean {
    // using 4 splitting planes to rule out intersections

    return !(
      box.max.x < this.min.x ||
      box.min.x > this.max.x ||
      box.max.y < this.min.y ||
      box.min.y > this.max.y
    );
  }

  public clampPoint(point: Vector2, out: Vector2): Vector2 {
    return out.copy(point).clamp(this.min, this.max);
  }

  public distanceToPoint(point: Vector2): number {
    const clampedPoint = v1.copy(point).clamp(this.min, this.max);

    return clampedPoint.sub(point).length();
  }

  public intersect(box: Box2): this {
    this.min.max(box.min);
    this.max.min(box.max);

    return this;
  }

  public union(box: Box2): this {
    this.min.min(box.min);
    this.max.max(box.max);

    return this;
  }

  public translate(offset: Vector2): this {
    this.min.add(offset);
    this.max.add(offset);

    return this;
  }

  public equals(box: Box2): boolean {
    return box.min.equals(this.min) && box.max.equals(this.max);
  }

  public get width(): number {
    return this.max.x - this.min.x;
  }

  public get height(): number {
    return this.max.y - this.min.y;
  }
}

const v1 = new Vector2();
